import React, { useEffect } from 'react';
import { Container } from '../components/ui/Container/Container';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';

export function RefundPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <main className="py-16">
        <Container size="md">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-foreground mb-4">Refund &amp; Cancellation Policy</h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: March 19, 2026
                </p>
              </div>

              <div className="prose prose-lg max-w-none">

                {/* 1. Overview */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">1. Overview</h2>
                  <p className="text-muted-foreground mb-4">
                    Cascady is a credit-based AI generation platform operated by Zero Point Studio d.o.o.
                    When you use credits to generate images or video, those credits are consumed immediately and cannot be recovered.
                    This policy outlines our approach to refunds and cancellations.
                  </p>
                  <p className="text-muted-foreground">
                    By subscribing to Cascady, you acknowledge and agree to this Refund &amp; Cancellation Policy.
                  </p>
                </section>

                {/* 2. Cancellation */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">2. Cancellation</h2>
                  <p className="text-muted-foreground mb-4">
                    You may cancel your subscription at any time. There are no cancellation fees or lock-in periods.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Cancel through the Stripe customer portal, accessible from your account settings</li>
                    <li>Your subscription remains active until the end of the current billing period</li>
                    <li>You retain access to all features and remaining credits until the period ends</li>
                    <li>No further charges will be applied after cancellation</li>
                    <li>Cancellation does not trigger a refund for the current billing period</li>
                  </ul>
                </section>

                {/* 3. General Refund Policy */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">3. General Refund Policy</h2>
                  <p className="text-muted-foreground mb-4">
                    Due to the nature of our credit-based service, <strong>refunds are generally not available</strong>.
                    When you subscribe and use credits for AI generations, those resources are consumed immediately through
                    third-party AI model providers — we incur costs for each generation that cannot be reversed.
                  </p>
                  <p className="text-muted-foreground">
                    For this reason, we cannot offer full or partial refunds based on the number of credits used or remaining
                    in your account.
                  </p>
                </section>

                {/* 4. Exceptions */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">4. Exceptions — When We Will Refund</h2>
                  <p className="text-muted-foreground mb-4">We will issue refunds in the following circumstances:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>
                      <strong>Service Cessation:</strong> If we are unable to continue operating the Service or choose to
                      permanently discontinue it, we will refund all unused credits on a pro-rata basis, regardless of when
                      they were purchased.
                    </li>
                    <li>
                      <strong>Extended Outage:</strong> If the Service experiences a prolonged outage (24+ hours) that prevents
                      you from using your credits during your billing period, we may issue credit compensation or a partial
                      refund at our discretion.
                    </li>
                    <li>
                      <strong>Billing Errors:</strong> If you are charged incorrectly due to a technical error on our part,
                      we will refund the incorrect charge in full.
                    </li>
                    <li>
                      <strong>Legal Requirement:</strong> Where required by applicable consumer protection law in your jurisdiction.
                    </li>
                  </ul>
                </section>

                {/* 5. Non-Refundable Scenarios */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">5. Non-Refundable Scenarios</h2>
                  <p className="text-muted-foreground mb-4">Refunds will not be issued for:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Credits that have been consumed for AI generations</li>
                    <li>Credits that have expired at the end of a billing cycle</li>
                    <li>Unused time remaining on a cancelled subscription (access continues until period end)</li>
                    <li>Dissatisfaction with AI model output quality (outputs depend on third-party AI providers)</li>
                    <li>Account suspension or termination due to violation of our Terms of Service or Acceptable Use Policy</li>
                    <li>Promotional, bonus, or free credits</li>
                    <li>Failure to cancel before a renewal date</li>
                    <li>Changes in pricing or plan features after reasonable notice was provided</li>
                    <li>Issues caused by your own equipment, internet connection, or browser compatibility</li>
                  </ul>
                </section>

                {/* 6. Subscription Plans */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">6. Subscription-Specific Terms</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">6.1 Monthly Plans</h3>
                  <p className="text-muted-foreground mb-4">
                    Monthly subscriptions are billed at the start of each billing cycle. Cancellation stops the next renewal.
                    No partial refunds are issued for the remainder of a billing period.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">6.2 Annual Plans</h3>
                  <p className="text-muted-foreground">
                    Annual subscriptions are billed upfront for the full year. Credits are distributed monthly.
                    If you cancel, you retain access through the end of the prepaid annual period. No partial refunds are
                    issued for the unused portion of an annual plan.
                  </p>
                </section>

                {/* 7. EU Consumer Rights */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">7. EU Consumer Rights</h2>
                  <p className="text-muted-foreground mb-4">
                    If you are a consumer in the European Union, you may have a 14-day right of withdrawal under the Consumer Rights Directive.
                    However, by using the Service and consuming credits during this period, you acknowledge that the digital service has been
                    fully performed, and you waive your right of withdrawal for credits that have been consumed.
                  </p>
                  <p className="text-muted-foreground">
                    Nothing in this policy limits your rights under applicable EU consumer protection laws.
                  </p>
                </section>

                {/* 8. Chargeback Policy */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">8. Chargeback Policy</h2>
                  <p className="text-muted-foreground">
                    If you dispute a charge directly with your bank or credit card company (a "chargeback") without first
                    contacting us, your account may be suspended pending investigation. We encourage you to contact us first
                    so we can resolve any billing issues directly. Fraudulent chargebacks may result in permanent account termination.
                  </p>
                </section>

                {/* 9. How to Request a Refund */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">9. How to Request a Refund</h2>
                  <p className="text-muted-foreground mb-4">
                    If you believe you qualify for a refund under the exceptions listed in Section 4, please contact us:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Email <strong>andrej@zeropointstudio.io</strong> with the subject line "Refund Request"</li>
                    <li>Include your account email and a description of the issue</li>
                    <li>We will respond within 3 business days</li>
                    <li>Approved refunds are processed within 5–10 business days to the original payment method</li>
                  </ul>
                </section>

                {/* 10. Policy Changes */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">10. Changes to This Policy</h2>
                  <p className="text-muted-foreground">
                    We may update this Refund &amp; Cancellation Policy from time to time. Material changes will be communicated
                    via email or a prominent notice on the Service. This policy is part of and subject to our{' '}
                    <a href="/terms" className="text-primary-600 underline">Terms of Service</a>.
                  </p>
                </section>

                {/* 11. Contact */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">11. Contact</h2>
                  <p className="text-muted-foreground mb-4">
                    For billing questions, refund requests, or cancellation assistance:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Company:</strong> Zero Point Studio d.o.o.</p>
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> andrej@zeropointstudio.io</p>
                    <p className="text-muted-foreground mb-2"><strong>Address:</strong> Rudeška cesta 179, 10000 Zagreb, Croatia</p>
                    <p className="text-muted-foreground"><strong>Phone:</strong> +385 91 722 8780</p>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </Container>
      </main>

      <PublicFooter />
    </div>
  );
}
